import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/util";
import {
  ShieldAlert, Loader2, RefreshCw, ExternalLink, CheckCircle2, AlertTriangle,
} from "lucide-react";

/**
 * KEV catalog panel — shown in /scanners for admins.
 *
 * Reads GET /api/kev/status (all authed users can hit it, but the panel is
 * gated to admin in the parent) and, when the admin taps the button, POSTs
 * to /api/kev/sync. The response contains both the sync summary and the
 * number of findings re-tagged.
 */
export function KevCatalogPanel({ canSync = true }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/kev/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    setLastResult(null);
    try {
      const r = await api.post("/kev/sync");
      setLastResult(r.data);
      const s = r.data?.sync || {};
      const t = r.data?.tag || {};
      if (s.ok) {
        toast.success(
          `KEV sincronizado · v${s.catalog_version} · ${s.count} CVEs · ` +
          `${t.flagged || 0} nuevos hallazgos marcados`
        );
      } else {
        toast.error(`Sync falló: ${s.reason || s.error || "error desconocido"}`);
      }
      await load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo sincronizar");
    } finally { setSyncing(false); }
  };

  return (
    <section className="border border-sev-critical/40 bg-sev-critical/5 p-4 space-y-3" data-testid="kev-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-4 h-4 text-sev-critical" />
          <div>
            <div className="overline text-sev-critical">CISA KEV · Catálogo local</div>
            <div className="font-display text-base">
              Vulnerabilidades activamente explotadas
            </div>
            <div className="mt-0.5">
              {loading ? (
                <span className="text-muted-foreground font-mono text-xs">Cargando…</span>
              ) : status ? (
                <span className="font-mono text-xs" data-testid="kev-status-line">
                  <span className="text-foreground font-bold">{(status.count ?? 0).toLocaleString()}</span>
                  {" CVEs · versión "}
                  <span className="text-primary">{status.catalog_version || "—"}</span>
                  {status.stale
                    ? <span className="text-sev-medium"> · desactualizado</span>
                    : <span className="text-sev-low"> · fresco</span>}
                </span>
              ) : (
                <span className="text-sev-critical font-mono text-xs">Estado no disponible</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={syncNow}
          disabled={syncing || loading || !canSync}
          data-testid="kev-sync-btn"
          title={canSync ? "Fuerza una sincronización inmediata (admin)" : "Solo administradores pueden sincronizar"}
          className={cn(
            "px-4 py-2 flex items-center gap-2 text-xs font-mono uppercase tracking-widest border transition-colors",
            syncing || !canSync
              ? "border-border text-muted-foreground cursor-not-allowed"
              : "border-sev-critical text-sev-critical hover:bg-sev-critical hover:text-background"
          )}
        >
          {syncing
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <RefreshCw className="w-3 h-3" />}
          {syncing ? "Sincronizando…" : "Sincronizar ahora"}
        </button>
      </div>

      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
          <Stat label="Última sync" value={status.last_success_at
            ? new Date(status.last_success_at).toLocaleString()
            : "nunca"} data-testid="kev-last-sync" />
          <Stat label="Fecha release" value={status.date_released
            ? new Date(status.date_released).toLocaleDateString()
            : "—"} />
          <Stat label="Entradas" value={(status.count ?? 0).toLocaleString()} accent />
          <Stat label="Estado" value={status.stale ? "desactualizado" : "al día"}
                tone={status.stale ? "medium" : "low"} />
        </div>
      )}

      {status?.last_error && (
        <div className="border border-sev-critical/40 bg-sev-critical/5 text-sev-critical text-[11px] font-mono p-2 flex items-start gap-2"
             data-testid="kev-last-error">
          <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>Último error: {status.last_error}</span>
        </div>
      )}

      {lastResult && lastResult.sync?.ok && (
        <div className="border border-sev-low/40 bg-sev-low/5 text-sev-low text-[11px] font-mono p-2 flex items-start gap-2"
             data-testid="kev-last-result">
          <CheckCircle2 className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>
            Sincronizado a v{lastResult.sync.catalog_version} · {lastResult.sync.count} CVEs ·
            {" "}{lastResult.tag?.flagged || 0} findings marcados, {lastResult.tag?.unflagged || 0} desmarcados,
            {" "}{lastResult.tag?.scanned || 0} escaneados
          </span>
        </div>
      )}

      <div className="text-[10px] font-mono text-muted-foreground">
        Fuente:{" "}
        <a href={status?.source_url || "https://www.cisa.gov/known-exploited-vulnerabilities-catalog"}
           target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1 text-primary hover:underline">
          cisa.gov/known_exploited_vulnerabilities.json <ExternalLink className="w-2.5 h-2.5" />
        </a>
        {" · Sync automática cada lunes 04:00 UTC + al arrancar backend si local >7 días."}
      </div>
    </section>
  );
}

function Stat({ label, value, accent, tone }) {
  const cls = tone === "medium" ? "text-sev-medium"
            : tone === "low"    ? "text-sev-low"
            : accent            ? "text-primary"
            : "text-foreground";
  return (
    <div className="border border-border p-2 bg-background/60">
      <div className="overline text-[9px]">{label}</div>
      <div className={cn("mt-0.5", cls)}>{value}</div>
    </div>
  );
}
